import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

async function enrichAiCodesWithDescriptions(
  codes: { opcs_4_code: string; ai_flag_text?: string | null }[] | { icd_10_code: string }[],
) {
  const isProcedures = codes.every((code) => "opcs_4_code" in code);
  if (isProcedures) {
    // get all the codes to lookup
    const codesToLookup = codes.map((code) => code.opcs_4_code);
    const { data: descriptions, error: descriptionsError } = await supabase
      .from("opcs4_codes")
      .select("code, description")
      .in("code", codesToLookup);

    if (descriptionsError) throw descriptionsError;

    // Map over original codes array to maintain order
    return codes.map((code) => {
      const dbCode = descriptions?.find((desc) => desc.code === code.opcs_4_code);

      if (!dbCode) {
        console.warn(`No description found for procedure code: ${code.opcs_4_code}`);
        return {
          code: code.opcs_4_code,
          description: "Unknown code - please inform Grant",
          aiFlagText: code.ai_flag_text,
        };
      }

      return {
        code: dbCode.code,
        description: dbCode.description || "",
        aiFlagText: code.ai_flag_text,
      };
    });
  }

  const isDiagnoses = codes.every((code) => "icd_10_code" in code);
  if (isDiagnoses) {
    // get all the codes to lookup
    const codesToLookup = codes.map((code) => code.icd_10_code);
    const { data: descriptions, error: descriptionsError } = await supabase
      .from("icd10_codes")
      .select("code, description")
      .in("code", codesToLookup);

    if (descriptionsError) throw descriptionsError;

    // Map over original codes array to maintain order
    return codes.map((code) => {
      const dbCode = descriptions?.find((desc) => desc.code === code.icd_10_code);

      if (!dbCode) {
        console.warn(`No description found for diagnosis code: ${code.icd_10_code}`);
        const isHistoricCode = "is_historic_code" in code && code.is_historic_code === true;
        return {
          code: code.icd_10_code,
          description: isHistoricCode ? "" : "Unknown code - please inform Grant",
        };
      }

      return {
        code: dbCode.code,
        description: dbCode.description || "",
      };
    });
  }

  throw new Error("Invalid codes - neither procedures nor diagnoses");
}

const withHistoricCodeFlags = (
  rows: { is_historic_code?: boolean }[],
  enriched: { code: string; description: string }[],
) => enriched.map((diagnosis, index) => ({ ...diagnosis, isHistoricCode: rows[index]?.is_historic_code ?? false }));

export const fetchLatestClassificationResult = async (encounterId: string) => {
  // fetch the latest classification result for the encounter
  const { data: classificationResult, error: classificationResultError } = await supabase
    .from("inbound_classification_results")
    .select(
      `
            *,
            inbound_endoscopy_specific_results(was_specimen_or_histology_taken, primary_diagnosis_identified),
            procedures:inbound_classification_result_procedures(*),
            diagnoses:inbound_classification_result_diagnosis(*)
          `,
    )
    .eq("encounter_id", encounterId)
    .order("created_at", { ascending: false })
    .limit(1)
    .single();
  if (classificationResultError) throw classificationResultError;
  if (!classificationResult) throw new Error("Classification result not found");

  const classificationResultProcedures = await enrichAiCodesWithDescriptions(
    classificationResult.procedures.sort((a, b) => a.order_index - b.order_index),
  );

  const sortedDiagnoses = classificationResult.diagnoses.sort((a, b) => a.order_index - b.order_index);
  const classificationResultDiagnoses = withHistoricCodeFlags(
    sortedDiagnoses,
    await enrichAiCodesWithDescriptions(sortedDiagnoses),
  );

  return {
    id: classificationResult.id,
    runId: classificationResult.run_id,
    langfuseTraceId: classificationResult.langfuse_trace_id,
    hrgId: classificationResult.hrg_id,
    outcome: classificationResult.outcome,
    isFollowUp: classificationResult.is_follow_up,
    humanFlagForReview: classificationResult.human_flag_for_review,
    aiFlagForReview: classificationResult.ai_flag_for_review,
    aiFlagForReviewText: classificationResult.ai_flag_for_review_text ?? null,
    classifiedAt: classificationResult.classified_at,
    endoscopyResult: classificationResult.inbound_endoscopy_specific_results
      ? {
          primaryDiagnosisIdentified:
            classificationResult.inbound_endoscopy_specific_results.primary_diagnosis_identified ?? null,
          wasSpecimenOrHistologyTaken:
            classificationResult.inbound_endoscopy_specific_results.was_specimen_or_histology_taken,
        }
      : undefined,
    procedures: classificationResultProcedures,
    diagnoses: classificationResultDiagnoses,
  };
};

export const fetchClassificationResultById = async (classificationResultId: string) => {
  const { data: classificationResult, error: classificationResultError } = await supabase
    .from("inbound_classification_results")
    .select(
      `
            *,
            inbound_endoscopy_specific_results(was_specimen_or_histology_taken, primary_diagnosis_identified),
            procedures:inbound_classification_result_procedures(*),
            diagnoses:inbound_classification_result_diagnosis(*)
          `,
    )
    .eq("id", classificationResultId)
    .single();

  if (classificationResultError) throw classificationResultError;
  if (!classificationResult) throw new Error("Classification result not found");

  const classificationResultProcedures = await enrichAiCodesWithDescriptions(
    classificationResult.procedures.sort((a, b) => a.order_index - b.order_index),
  );

  const sortedDiagnoses = classificationResult.diagnoses.sort((a, b) => a.order_index - b.order_index);
  const classificationResultDiagnoses = withHistoricCodeFlags(
    sortedDiagnoses,
    await enrichAiCodesWithDescriptions(sortedDiagnoses),
  );

  return {
    id: classificationResult.id,
    runId: classificationResult.run_id,
    langfuseTraceId: classificationResult.langfuse_trace_id,
    hrgId: classificationResult.hrg_id,
    outcome: classificationResult.outcome,
    isFollowUp: classificationResult.is_follow_up,
    humanFlagForReview: classificationResult.human_flag_for_review,
    aiFlagForReview: classificationResult.ai_flag_for_review,
    aiFlagForReviewText: classificationResult.ai_flag_for_review_text ?? null,
    classifiedAt: classificationResult.classified_at,
    endoscopyResult: classificationResult.inbound_endoscopy_specific_results
      ? {
          primaryDiagnosisIdentified:
            classificationResult.inbound_endoscopy_specific_results.primary_diagnosis_identified ?? null,
          wasSpecimenOrHistologyTaken:
            classificationResult.inbound_endoscopy_specific_results.was_specimen_or_histology_taken,
        }
      : undefined,
    procedures: classificationResultProcedures,
    diagnoses: classificationResultDiagnoses,
  };
};

const useLatestClassificationResult = (encounterId: string) => {
  const latestClassificationResultQuery = useQuery({
    queryKey: ["latest-classification-result", encounterId],
    queryFn: () => fetchLatestClassificationResult(encounterId),
  });

  return latestClassificationResultQuery;
};

export default useLatestClassificationResult;
